'use client'

import { useEffect, useRef, useState } from 'react'
import Sentry from '@/lib/sentry-safe'
import { useCookieConsent } from './cookie-consent'

// AdSense configuration - set your publisher ID in .env
const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT || ''

/**
 * adsbygoogle.push({}) does not target the <ins> the caller rendered — it fills
 * the FIRST unprocessed `ins.adsbygoogle` in document order. When that element
 * measures zero width, AdSense throws
 * `TagError: No slot size for availableWidth=0` and the impression is lost.
 *
 * Two zero-width elements reach that position in production: the auto-ads
 * anchor slot Google injects itself (observed as a 0x0 `ins.adsbygoogle` with
 * no data-ad-slot), and a slot mid-teardown during a client-side navigation.
 * Verified 2026-08-26 (TES-1876): planting a 0-width `<ins>` ahead of the
 * homepage slot leaves the homepage slot at data-adsbygoogle-status=null with
 * no iframe — it never fills.
 */
const MIN_SLOT_WIDTH_PX = 1

/**
 * How long to wait, from the first attempt that is blocked, before forcing the
 * issue — either pushing into our own still-zero-width slot, or spending a push
 * to clear a foreign zero-width slot that is sitting in front of it.
 */
const BLOCKED_WAIT_TIMEOUT_MS = 10_000

/**
 * How long the pre-fill reservation is held before the space is given back.
 * Deliberately separate from the push machinery's deadlines: this one is armed
 * when the slot becomes visible and is about layout, not about serving.
 */
const RESERVATION_TIMEOUT_MS = 10_000

/**
 * Poll cadence, and how many polls to keep taking *after* the AdSense global
 * exists. The wait for the global itself is deliberately uncapped: AdSenseLoader
 * only injects the script on a real interaction and has no time fallback
 * (TES-858/TES-840), so a user who reads for a minute before their first tap
 * would otherwise find the slot already given up on. Until then each poll is a
 * property read, and the effect cleanup stops it on unmount.
 */
const POLL_INTERVAL_MS = 250
const POLL_MAX_ATTEMPTS = 240 // ~60s after the script lands

/**
 * Pushes spent clearing zero-width slots that sit ahead of ours. `push()`
 * resolves normally even when the slot it claimed is unusable — the TagError is
 * raised asynchronously inside adsbygoogle.js, so there is nothing to catch —
 * but the claim itself is real: AdSense stamps `data-adsbygoogle-status` on a
 * slot it failed. Spending a push therefore clears the blocker and moves ours
 * to the front. Kept small because each one also consumes a slot.
 */
const MAX_CLEARING_PUSHES = 2

/** The element adsbygoogle.push({}) will actually fill, if any. */
function nextUnfilledSlot(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>('ins.adsbygoogle')
  for (const node of nodes) {
    if (!node.getAttribute('data-adsbygoogle-status')) return node
  }
  return null
}

interface AdPlacementProps {
  slot: string
  /**
   * The shape asked of AdSense. A request, not a guarantee: on a responsive
   * unit it binds at `sm` and up and is advisory below that, where
   * `data-full-width-responsive` outranks it (TES-2325). Read `reservedShape`
   * for the shape the layout actually holds space for.
   */
  format?: 'auto' | 'horizontal' | 'vertical' | 'rectangle'
  className?: string
  style?: React.CSSProperties
  responsive?: boolean
  /** Use intersection observer to lazy-load below-fold ads */
  lazy?: boolean
}

/**
 * AdSense ad placement component
 *
 * Fixes over original implementation:
 * - hasPushedRef prevents multiple adsbygoogle.push({}) race conditions
 * - Intersection observer for lazy loading below-fold ads
 * - Error handling for script load failures
 * - min-height CSS reserves space to prevent CLS
 */
function AdPlacement({
  slot,
  format = 'auto',
  className = '',
  style,
  responsive = true,
  lazy = false
}: AdPlacementProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const insRef = useRef<HTMLModElement>(null)
  const hasPushedRef = useRef(false)
  const [isVisible, setIsVisible] = useState(!lazy)
  const [adError, setAdError] = useState(false)
  const [reservationExpired, setReservationExpired] = useState(false)
  const { marketingAllowed } = useCookieConsent()

  /**
   * Stop holding the pre-fill reservation after a bounded wait (TES-1965).
   *
   * Waiting to *serve* is uncapped on purpose (see POLL_MAX_ATTEMPTS) and that
   * is right — a late impression is still an impression. Holding *layout* is a
   * different question with a different answer: the mobile slot reserves a
   * viewport square, and for a reader whose blocker ate the script that is a
   * 412px hole in the homepage that nothing will ever fill. `adError` does not
   * cover them either — with the script blocked `window.adsbygoogle` never
   * appears, so the poll early-returns forever and never reaches its own
   * give-up path.
   *
   * So give the space back once the wait stops looking normal. If an ad does
   * land afterwards it expands then; a late shift beats a permanent hole.
   */
  useEffect(() => {
    if (!isVisible || reservationExpired) return
    const timer = setTimeout(() => {
      // AdSense stamps this the moment it answers, filled or not. React is
      // never told, so read the DOM rather than trusting state here.
      if (!insRef.current?.hasAttribute('data-ad-status')) {
        setReservationExpired(true)
      }
    }, RESERVATION_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isVisible, reservationExpired])

  // Intersection observer for lazy loading below-fold ads
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setIsVisible here triggers the ad-push effect; two-phase lazy-load pattern is intentional
  useEffect(() => {
    if (!lazy || isVisible) return

    const el = containerRef.current
    if (!el) {
      // Retry after a short delay if element not yet mounted
      const retryTimer = setTimeout(() => {
        const retryEl = containerRef.current
        if (retryEl && !isVisible) {
          setIsVisible(true)
        }
      }, 100)
      return () => clearTimeout(retryTimer)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [lazy, isVisible])

  // Push ad once visible, laid out with a real width, and the AdSense script is
  // loaded (by AdSenseLoader).
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setAdError here is an error path only; not a render-triggered cascade
  useEffect(() => {
    if (!isVisible || !marketingAllowed || !ADSENSE_CLIENT || !slot || hasPushedRef.current) {
      return
    }

    let disposed = false
    let attempts = 0
    let forcing = false
    let clearingPushes = 0
    let resizeObserver: ResizeObserver | null = null
    let poll: ReturnType<typeof setInterval> | null = null
    let blockedDeadline: ReturnType<typeof setTimeout> | null = null
    let script: HTMLScriptElement | null = null

    const teardown = () => {
      resizeObserver?.disconnect()
      resizeObserver = null
      if (poll) clearInterval(poll)
      poll = null
      if (blockedDeadline) clearTimeout(blockedDeadline)
      blockedDeadline = null
      script?.removeEventListener('load', tryPush)
      script?.removeEventListener('error', onScriptError)
      script = null
    }

    function onScriptError() {
      if (disposed) return
      // Ad blockers are the overwhelming cause of a failed AdSense script load.
      // That is a user-environment condition, not a defect worth an event.
      setAdError(true)
      teardown()
    }

    /**
     * Arm the give-up timer on the first *blocked* attempt rather than on
     * mount. AdSenseLoader gates the script behind a real interaction, so it
     * routinely lands several seconds after the slot becomes visible; a timer
     * started at mount would expire while we were still waiting on the script.
     */
    function armDeadline() {
      blockedDeadline ??= setTimeout(() => {
        forcing = true
        tryPush()
      }, BLOCKED_WAIT_TIMEOUT_MS)
    }

    function push() {
      try {
        window.adsbygoogle.push({})
        return true
      } catch (error) {
        setAdError(true)
        teardown()
        Sentry.captureException(error, {
          tags: { context: 'AdPlacement', action: 'pushAd', slot }
        })
        return false
      }
    }

    function tryPush() {
      if (disposed || hasPushedRef.current || !window.adsbygoogle) return

      const el = insRef.current
      if (!el?.isConnected) return

      // The only trustworthy signal that a push landed on our element. push()
      // itself tells us nothing: it resolves normally even when the slot it
      // claimed was unusable, because the TagError comes from adsbygoogle.js
      // asynchronously. Verified on production 2026-08-26 (TES-1876).
      if (el.getAttribute('data-adsbygoogle-status')) {
        hasPushedRef.current = true
        teardown()
        return
      }

      const target = nextUnfilledSlot()

      if (target === el) {
        // Ours is next in line. Hold while it is still collapsed, since a
        // zero-width claim is what produces `No slot size for availableWidth=0`.
        if (!forcing && el.getBoundingClientRect().width < MIN_SLOT_WIDTH_PX) {
          armDeadline()
          return
        }
        if (push()) hasPushedRef.current = true
        teardown()
        return
      }

      // Something else is ahead of us and would absorb this push.
      if (!target || target.getBoundingClientRect().width >= MIN_SLOT_WIDTH_PX) {
        // A real slot — another placement's, and its own effect will claim it.
        // Nothing to do but let the poll pick us up once it has.
        return
      }

      // A zero-width slot ahead of ours: Google's auto-ads anchor, or one
      // mid-teardown during a client-side navigation. It usually resolves on
      // its own within a second or two, so wait first — see MAX_CLEARING_PUSHES
      // for why spending a push is the fallback rather than the first move.
      if (!forcing) {
        armDeadline()
        return
      }
      if (clearingPushes >= MAX_CLEARING_PUSHES) {
        teardown()
        return
      }
      clearingPushes++
      push()
      // Deliberately not marking ourselves pushed: that push went to the
      // blocker. The next poll re-checks who is at the front now.
    }

    // Re-check as soon as our slot gains a width (mount, transition, reflow).
    if (insRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => tryPush())
      resizeObserver.observe(insRef.current)
    }

    const watchScript = () => {
      if (script) return
      script = document.querySelector<HTMLScriptElement>(
        'script[src*="pagead2.googlesyndication.com"]'
      )
      script?.addEventListener('load', tryPush)
      script?.addEventListener('error', onScriptError)
    }

    // Covers the AdSense script arriving late and an earlier zero-width slot
    // being consumed by someone else.
    poll = setInterval(() => {
      watchScript()
      // Don't spend the attempt budget waiting on an interaction-gated script.
      if (!window.adsbygoogle) return
      if (++attempts >= POLL_MAX_ATTEMPTS) {
        teardown()
        return
      }
      tryPush()
    }, POLL_INTERVAL_MS)

    watchScript()

    tryPush()

    return () => {
      disposed = true
      teardown()
    }
  }, [isVisible, marketingAllowed, slot])

  // Don't render if no consent or not configured
  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  // Sizes are only the pre-fill placeholder: any unit carrying `data-ad-format`
  // is a responsive unit and AdSense rewrites width/height on fill. Pinning a
  // height on the responsive shapes just guarantees the rewrite is a jump.
  const formatStyles: Record<string, React.CSSProperties> = {
    auto: { display: 'block' },
    horizontal: { display: 'block' },
    vertical: { display: 'block', width: '160px', height: '600px' },
    rectangle: { display: 'block', width: '300px', height: '250px' }
  }

  /**
   * What we reserve space for, which is deliberately not what we ask AdSense
   * for (TES-2325).
   *
   * `horizontal` is the request. Below `sm` it is advisory only — AdSense
   * serves a viewport-width square instead, every sample, both themes
   * (375x375, 390x390, 412x412) — so the reservation there is a square. Keyed
   * under a selector called "horizontal" that read as a contradiction: the CSS
   * said one shape and reserved another. The reservation is keyed on `band`
   * instead — one placement, both of its shapes — leaving the word horizontal
   * only on `data-ad-format`, the one place it means anything.
   */
  const reservedShape = format === 'horizontal' ? 'band' : format

  return (
    <div
      ref={containerRef}
      className={`ad-container ${className}`}
      // Drives the pre-fill reservation in globals.css, keyed on the shape we
      // reserve rather than the one we request — those differ below `sm`, where
      // `data-full-width-responsive` wins and the reservation is sized against
      // the measured mobile shape (TES-1965, TES-2325).
      data-ad-shape={reservedShape}
      // We have stopped waiting — the script failed, or the reservation window
      // closed with no answer. Either way, release the space rather than leave
      // a viewport-tall hole nothing will ever fill (TES-1965).
      data-ad-unresolved={adError || reservationExpired ? 'true' : undefined}
      // width is explicit so the slot can never measure zero inside a flex or
      // grid ancestor that would otherwise shrink it to its content (TES-1876).
      style={{ width: '100%', ...style }}
    >
      {isVisible && !adError && (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{
            ...formatStyles[format],
            ...(responsive && { width: '100%' })
          }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          // The requested shape, not the 'auto' this used to force. Measured on
          // production: at desktop widths this binds (1280px serves 942x90 into
          // the 90px reserved above, zero shift). Below `sm` it does not —
          // `data-full-width-responsive` outranks it and the unit comes back a
          // viewport-width square regardless, which is why the mobile
          // reservation is measured rather than derived from this value.
          data-ad-format={format}
          data-full-width-responsive={responsive ? 'true' : 'false'}
        />
      )}
    </div>
  )
}

/**
 * §08.1's quarantine, as one class list: 1px hairline frame, quarantine ground,
 * SQUARE corners. The one surface the no-card rule exempts is a frame, not a
 * rounded card, and §08.1 grants it to every slot — the placement map varies
 * *where* an ad may appear, never whether it is quarantined once it does.
 *
 * Framing is deliberately breakpoint-split (TES-1965). `data-full-width-
 * responsive` lets AdSense widen a mobile creative to the viewport and shift it
 * back with a negative margin, so below `sm` the creative is always edge-to-edge
 * no matter how much padding sits above it. A bordered, inset card there gets
 * sliced through both side borders. Measured again for the in-feed slot on
 * production at 375px (logs/tes-2328/before-prod/report.json): the container is
 * 343px and the creative is 375px, so it overhangs by exactly the page gutter.
 * So the band goes full-bleed at mobile and is closed with hairlines top and
 * bottom; the side hairlines return at `sm`, where the creative stays inside the
 * container.
 *
 * Whichever component carries this owns the *only* frame its slot gets —
 * callers pass spacing, not chrome.
 */
const QUARANTINE_BAND =
  'tls-ad-band rounded-[var(--ls-radius-0)] border-y border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] py-4 max-sm:mx-[calc(50%-50vw)] sm:border-x sm:px-4'

/**
 * The full-width sponsored band, with quiet design-system styling and a
 * "Sponsored" label. Used on the homepage, sound detail, blog posts, /convert,
 * /guide and the category and model hubs.
 *
 * "Horizontal" is the shape it asks for and the shape it gets at `sm` and up
 * (1280px serves 942x90). **Below `sm` it is a viewport-width square, edge to
 * edge** — 375x375 measured on production, both themes, at the surviving detail
 * slot (logs/tes-2325/baseline-prod/report.json). That is AdSense answering
 * `data-full-width-responsive` ahead of the format hint, and it is accepted
 * rather than fought: the square is the higher-earning mobile shape and
 * constraining it trades fill rate for tidiness (TES-2325). So read this
 * component as "the band", not "a 90px banner" — on a phone it is the largest
 * single block on the page, and a caller planning a layout around it needs the
 * square, not the hint.
 *
 * `InFeedAd` below serves the same square from the same slot ids, which is why
 * both reserve under `data-ad-shape="band"` rather than the requested name.
 */
export function HorizontalAd({
  slot,
  className = '',
  lazy = false
}: {
  slot: string
  className?: string
  lazy?: boolean
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <div className={`${QUARANTINE_BAND} ${className}`}>
      <div className="mb-2 text-center text-xs text-[var(--tls-fg-subtle)]">Sponsored</div>
      <AdPlacement slot={slot} format="horizontal" responsive lazy={lazy} />
    </div>
  )
}

/**
 * In-feed ad for between content items. Quarantined the same as every other
 * slot — 1px hairline frame, quarantine ground, square corners — per §08.1.
 *
 * The frame matters more here than on the banded slot, not less. This one lands
 * in a list of rows, and the unit AdSense serves it at 375px is itself a list of
 * rows: a white panel headed "Discover more" with chevrons down the right edge
 * (logs/tes-2328/before-prod/viewport-375.png). Unframed, on a page whose entire
 * content model is a row, that is the failure §08.1 names outright — paid
 * content reading as product.
 */
export function InFeedAd({
  slot,
  className = '',
  lazy = true
}: {
  slot: string
  className?: string
  lazy?: boolean
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <div className={`${QUARANTINE_BAND} my-6 ${className}`}>
      <div className="mb-1 text-center text-xs text-[var(--tls-fg-subtle)]">Advertisement</div>
      <AdPlacement slot={slot} format="horizontal" responsive lazy={lazy} />
    </div>
  )
}

/**
 * Sidebar ad for vertical placements
 */
function SidebarAd({
  slot,
  className = ''
}: {
  slot: string
  className?: string
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <div className={`sticky top-24 ${className}`}>
      <div className="rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] p-4">
        <div className="mb-2 tls-eyebrow">Advertisement</div>
        <AdPlacement slot={slot} format="rectangle" responsive={false} />
      </div>
    </div>
  )
}

/**
 * Placeholder ad component for development/preview
 * Shows what an ad would look like without actually loading ads
 */
function AdPlaceholder({
  format = 'horizontal',
  className = ''
}: {
  format?: 'horizontal' | 'vertical' | 'rectangle'
  className?: string
}) {
  const sizes = {
    horizontal: 'h-24 w-full',
    vertical: 'w-40 h-[600px]',
    rectangle: 'w-[300px] h-[250px]'
  }

  return (
    <div className={`${sizes[format]} ${className}`}>
      <div className="flex size-full items-center justify-center rounded-[var(--ls-radius-0)] border border-dashed border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]">
        <div className="text-center text-sm text-[var(--tls-fg-subtle)]">
          <div className="mb-1">Ad Space</div>
          <div className="text-xs">{format}</div>
        </div>
      </div>
    </div>
  )
}

// Add TypeScript declaration for window.adsbygoogle
declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}
